import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, RefreshCw, Copy } from "lucide-react";
import { mfaApi } from "../../services/mfaApi";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";

export default function SecuritySettingsPage() {
  const { t } = useLocalization();
  const [status, setStatus] = useState(null);
  const [showRegenForm, setShowRegenForm] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [newCodes, setNewCodes] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    mfaApi.status()
      .then((r) => setStatus(r.data))
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); });
  }, []);

  const handleRegenerate = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await mfaApi.regenerateBackupCodes(password);
      setNewCodes(data.backup_codes);
      setShowRegenForm(false);
      setPassword("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newCodes.join("\n"));
    toast.success(t("mfa.codesCopied"));
  };

  if (accessDenied) return <AccessDenied resourceLabel={t("mfa.securityTitle")} />;
  if (!status) return null;

  return (
    <div className="max-w-lg" data-testid="security-settings-page">
      <PageHelpBanner pageKey="security-settings" title="How to use this page">
        <p>This is your own account's two-factor security, separate from Access Profiles (which control other users' permissions). If you ever lose your authenticator app, use a backup code to sign in, then come back here to regenerate a fresh set.</p>
      </PageHelpBanner>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-6">{t("mfa.securityTitle")}</h2>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-1">
          {status.mfa_enabled ? (
            <ShieldCheck className="text-emerald-500" size={22} />
          ) : (
            <ShieldOff className="text-gray-500" size={22} />
          )}
          <span className="font-semibold text-gray-900" data-testid="mfa-status-label">
            {status.mfa_enabled ? t("mfa.mfaEnabled") : t("mfa.mfaDisabled")}
          </span>
        </div>
        {status.mfa_enrolled_at && (
          <p className="text-xs text-gray-500 ml-8">{new Date(status.mfa_enrolled_at).toLocaleString()}</p>
        )}
      </div>

      {status.mfa_enabled && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {newCodes ? (
            <div data-testid="regenerated-backup-codes-panel">
              <p className="text-sm font-semibold text-gray-900 mb-3">{t("mfa.backupCodesTitle")}</p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 gap-2 font-mono text-sm text-gray-800" data-testid="regenerated-backup-codes-list">
                {newCodes.map((c) => <span key={c}>{c}</span>)}
              </div>
              <button
                data-testid="copy-regenerated-codes-button"
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 active:scale-95 transition-all"
              >
                <Copy size={14} /> {t("mfa.copyAllCodes")}
              </button>
            </div>
          ) : showRegenForm ? (
            <form onSubmit={handleRegenerate} className="space-y-3" data-testid="regenerate-backup-codes-form">
              <p className="text-xs text-gray-500">{t("mfa.regenerateWarning")}</p>
              <input
                data-testid="regenerate-current-password-input"
                type="password"
                required
                placeholder={t("mfa.currentPasswordLabel")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm"
              />
              {error && <p className="text-sm text-red-500" data-testid="regenerate-backup-codes-error">{error}</p>}
              <div className="flex gap-2">
                <button data-testid="confirm-regenerate-backup-codes-button" type="submit" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">
                  {t("common.submit")}
                </button>
                <button type="button" onClick={() => setShowRegenForm(false)} className="px-4 py-2 rounded-full border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                  {t("common.cancel")}
                </button>
              </div>
            </form>
          ) : (
            <button
              data-testid="regenerate-backup-codes-button"
              onClick={() => setShowRegenForm(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 active:scale-95 transition-all"
            >
              <RefreshCw size={14} /> {t("mfa.regenerateBackupCodes")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
